// apps/admin/src/features/audit/AuditLogTable.tsx
//
// Phase 8.3 Wave 5 Task 39 — Recent audit log table + filter bar.
//
// vg-binding: CONTEXT:decisions=P8.D-113,P8.D-191,P8.D-258,P8.D-480
// vg-binding: INTERFACE-STANDARDS:error-shape
//
// Renders the warm-tier (≤180d) audit log:
//   - 10-column table (Timestamp, Actor, Actor Role
//     Entity Type, Entity ID, Action, IP, User Agent, Diff count, Severity).
//   - Filter bar: actor_user_id (free text ObjectId), actor_role,
//     target_type (24-enum), action, date range, severity, q.
//   - Round-14 (2026-05-23): numbered pagination per BR-PAGING. Replaces
//     the cursor + "Tải thêm" buffer pattern. Parent owns `page` via URL
//     state; this component fetches one page per request and renders the
//     shared <Pagination> control.
//   - Row click → bubbles selected row to parent for the detail drawer.
//   - Empty state: centered card "Không có log entry nào" only when filter
//     yields zero rows AND not loading.
//
// URL state is owned by the parent page (AuditLogsPage) — this component is
// a controlled view receiving `filter` + `onFilterChange`.

import { useEffect, useMemo, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Pagination } from '@printway/ui';

import {
  AUDIT_V2_ACTOR_ROLES,
  AUDIT_V2_ENTITY_TYPES,
  AUDIT_V2_SEVERITY,
  listAuditLogWarm,
  type AuditV2ActorRole,
  type AuditV2ApiError,
  type AuditV2EntityType,
  type AuditV2Row,
  type AuditV2Severity,
  type AuditV2WarmFilter,
} from '../../api/audit-log-v2.api.js';
import { AUDIT_ACTION_OPTIONS } from '../../api/auditLog.api.js';
import { MultiSelectDropdown } from './MultiSelectDropdown.js';
import { actionLabel } from './action-label.js';
import { useTranslation } from '../../i18n/useTranslation.js';

type TFn = (key: string, vars?: Record<string, unknown>) => string;

// Shared audit enum maps (`audit.*`) — raw backend value kept as the
// i18next defaultValue so unmapped enums fall back to the raw string.
function entityTypeLabel(t: TFn, value: string): string {
  return t(`audit.entityTypeLabels.${value}`, { defaultValue: value });
}
function actorRoleLabel(t: TFn, value: string): string {
  return t(`audit.actorRoleLabels.${value}`, { defaultValue: value });
}
function severityLabel(t: TFn, value: string): string {
  return t(`audit.severityLabels.${value}`, { defaultValue: value });
}

// ---------------------------------------------------------------------------
// Local types
// ---------------------------------------------------------------------------

export type AuditWarmFilterValue = Omit<
  AuditV2WarmFilter,
  'cursor' | 'limit' | 'page'
>;

export interface AuditLogTableProps {
  filter: AuditWarmFilterValue;
  onFilterChange: (next: AuditWarmFilterValue) => void;
  onRowClick: (row: AuditV2Row) => void;
  /** 1-based page index from parent URL state. */
  page: number;
  onPageChange: (next: number) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtTimestamp(iso: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function fmtRelative(iso: string): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  return `${day}d`;
}

function truncate(value: string | null, max = 32): string {
  if (!value) return '—';
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1)}…`;
}

function severityBadge(sev: AuditV2Severity): string {
  switch (sev) {
    case 'security':
      return 'bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800';
    case 'warning':
      return 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800';
    case 'info':
    default:
      return 'bg-pw-surface text-pw-text border-pw-border';
  }
}

function isApiError(err: unknown): err is AuditV2ApiError {
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as { ok?: unknown }).ok === false &&
    typeof (err as { userMessage?: unknown }).userMessage === 'string'
  );
}

// ---------------------------------------------------------------------------
// Filter bar
// ---------------------------------------------------------------------------

interface FilterBarProps {
  value: AuditWarmFilterValue;
  onChange: (next: AuditWarmFilterValue) => void;
  disabled?: boolean;
}

function FilterBar({ value, onChange, disabled }: FilterBarProps): JSX.Element {
  const { t } = useTranslation('ops');
  function set<K extends keyof AuditWarmFilterValue>(
    key: K,
    next: AuditWarmFilterValue[K],
  ): void {
    onChange({ ...value, [key]: next });
  }

  function clear(): void {
    onChange({});
  }

  const targetTypeOptions = useMemo(
    () =>
      AUDIT_V2_ENTITY_TYPES.map((et) => ({
        value: et,
        label: entityTypeLabel(t, et),
      })),
    [t],
  );

  // Action options come from the legacy v1 catalog (35 codes); v2 BE accepts
  // arbitrary strings via the multi-value `action` filter, so this list is
  // just the curated common subset for muscle-memory. Free-text fallback is
  // intentionally left out — the `q` (search) field already covers ad-hoc
  // typing. Labels resolve through the shared `audit.actionLabels.*` map.
  const actionOptions = useMemo(
    () =>
      AUDIT_ACTION_OPTIONS.map((o) => ({
        value: o.value,
        label: t(`audit.actionLabels.${o.value}`, { defaultValue: o.label }),
      })),
    [t],
  );

  const hasAny =
    Boolean(value.actor_user_id) ||
    Boolean(value.actor_role) ||
    (value.target_type?.length ?? 0) > 0 ||
    (value.action?.length ?? 0) > 0 ||
    Boolean(value.date_from) ||
    Boolean(value.date_to) ||
    Boolean(value.severity) ||
    Boolean(value.q);

  return (
    <div
      className="flex flex-col gap-3 rounded-lg border border-pw-border bg-pw-bg p-3"
      data-testid="audit-warm-filters"
    >
      {/* Row 1 — multi-select dropdowns + actor user id (the wide row). */}
      <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
        <MultiSelectDropdown
          label={t('audit.filters.action')}
          options={actionOptions}
          value={value.action ?? []}
          onChange={(next) => set('action', next.length > 0 ? next : undefined)}
          placeholder={t('audit.filters.allActions')}
          testId="audit-filter-action"
          disabled={disabled}
        />
        <MultiSelectDropdown
          label={t('audit.filters.entityType')}
          options={targetTypeOptions}
          value={value.target_type ?? []}
          onChange={(next) =>
            set(
              'target_type',
              next.length > 0 ? (next as AuditV2EntityType[]) : undefined,
            )
          }
          placeholder={t('audit.filters.allEntities')}
          testId="audit-filter-entity-type"
          disabled={disabled}
        />
        <label className="flex flex-col gap-1 text-xs text-pw-text-muted">
          {t('audit.filters.actorUserId')}
          <input
            type="text"
            inputMode="text"
            placeholder={t('audit.filters.actorUserIdPlaceholder')}
            value={value.actor_user_id ?? ''}
            onChange={(e) =>
              set('actor_user_id', e.target.value.trim() || undefined)
            }
            disabled={disabled}
            className="h-9 rounded-md border border-pw-border bg-pw-bg px-2 font-mono text-sm text-pw-text"
          />
        </label>
      </div>

      {/* Row 2 — single-select narrowing + search + date range. */}
      <div className="grid grid-cols-1 gap-2 md:grid-cols-5">
        <label className="flex flex-col gap-1 text-xs text-pw-text-muted">
          {t('audit.filters.actorRole')}
          <select
            value={value.actor_role ?? ''}
            onChange={(e) =>
              set(
                'actor_role',
                (e.target.value || undefined) as AuditV2ActorRole | undefined,
              )
            }
            disabled={disabled}
            className="h-9 rounded-md border border-pw-border bg-pw-bg px-2 text-sm text-pw-text"
          >
            <option value="">{t('audit.filters.all')}</option>
            {AUDIT_V2_ACTOR_ROLES.map((r) => (
              <option key={r} value={r}>
                {actorRoleLabel(t, r)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-pw-text-muted">
          {t('audit.filters.severity')}
          <select
            value={value.severity ?? ''}
            onChange={(e) =>
              set(
                'severity',
                (e.target.value || undefined) as AuditV2Severity | undefined,
              )
            }
            disabled={disabled}
            className="h-9 rounded-md border border-pw-border bg-pw-bg px-2 text-sm text-pw-text"
          >
            <option value="">{t('audit.filters.all')}</option>
            {AUDIT_V2_SEVERITY.map((s) => (
              <option key={s} value={s}>
                {severityLabel(t, s)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-pw-text-muted">
          {t('audit.filters.dateFrom')}
          <input
            type="datetime-local"
            value={value.date_from ? value.date_from.slice(0, 16) : ''}
            onChange={(e) =>
              set(
                'date_from',
                e.target.value
                  ? new Date(e.target.value).toISOString()
                  : undefined,
              )
            }
            disabled={disabled}
            className="h-9 rounded-md border border-pw-border bg-pw-bg px-2 text-sm text-pw-text"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-pw-text-muted">
          {t('audit.filters.dateTo')}
          <input
            type="datetime-local"
            value={value.date_to ? value.date_to.slice(0, 16) : ''}
            onChange={(e) =>
              set(
                'date_to',
                e.target.value
                  ? new Date(e.target.value).toISOString()
                  : undefined,
              )
            }
            disabled={disabled}
            className="h-9 rounded-md border border-pw-border bg-pw-bg px-2 text-sm text-pw-text"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-pw-text-muted">
          {t('audit.filters.search')}
          <input
            type="search"
            placeholder={t('audit.filters.searchPlaceholder')}
            value={value.q ?? ''}
            onChange={(e) => set('q', e.target.value || undefined)}
            disabled={disabled}
            className="h-9 rounded-md border border-pw-border bg-pw-bg px-2 text-sm text-pw-text"
            data-testid="audit-filter-q"
          />
        </label>
      </div>

      {hasAny ? (
        <div>
          <button
            type="button"
            onClick={clear}
            disabled={disabled}
            className="text-xs font-medium text-pw-primary hover:underline disabled:opacity-50"
            data-testid="audit-filter-clear"
          >
            {t('audit.filters.clear')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

const PAGE_SIZE = 50;

export function AuditLogTable({
  filter,
  onFilterChange,
  onRowClick,
  page,
  onPageChange,
}: AuditLogTableProps): JSX.Element {
  const { t } = useTranslation('ops');
  const { t: tAdmin } = useTranslation('admin');
  // Round-14: numbered pagination. Each page fetch is independent — no
  // accumulating buffer. Auto-reset to page 1 whenever filters rotate
  // (parent owns the URL state; we just nudge via `onPageChange(1)`).
  const targetTypeKey = (filter.target_type ?? []).join(',');
  const actionKey = (filter.action ?? []).join(',');
  useEffect(() => {
    if (page !== 1) onPageChange(1);
    // Intentionally tracking only the filter signature — `page` + the
    // `onPageChange` callback are stable inputs we don't want in this
    // reset trigger (would loop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filter.actor_user_id,
    filter.actor_role,
    targetTypeKey,
    actionKey,
    filter.date_from,
    filter.date_to,
    filter.severity,
    filter.q,
  ]);

  const apiParams: AuditV2WarmFilter = useMemo(
    () => ({ limit: PAGE_SIZE, page, ...filter }),
    [filter, page],
  );

  const query = useQuery({
    queryKey: ['audit-log-v2', 'warm', apiParams],
    queryFn: () => listAuditLogWarm(apiParams, tAdmin),
    placeholderData: (prev) => prev,
  });

  const rows = query.data?.rows ?? [];
  const meta = query.data?.meta;

  const errMsg = useMemo<string | null>(() => {
    if (!query.error) return null;
    if (isApiError(query.error)) return query.error.userMessage;
    if (query.error instanceof Error) return query.error.message;
    return t('audit.errors.loadFailed');
  }, [query.error, t]);

  const isInitialLoading = query.isLoading && rows.length === 0;
  const isEmpty =
    !isInitialLoading && !query.isFetching && rows.length === 0 && !errMsg;

  return (
    <div className="flex flex-col gap-3">
      <FilterBar
        value={filter}
        onChange={onFilterChange}
        disabled={query.isFetching}
      />

      {errMsg ? (
        <div
          className="rounded-md border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/40 px-3 py-2 text-sm text-rose-800 dark:text-rose-300"
          data-testid="audit-warm-error"
          role="alert"
        >
          {errMsg}
        </div>
      ) : null}

      <section className="rounded-lg border border-pw-border bg-pw-bg">
        {!isInitialLoading && (rows.length > 0 || (meta?.total ?? 0) > 0) ? (
          <div className="border-b border-pw-border px-3 py-2">
            <Pagination
              currentPage={meta?.page ?? page}
              pageCount={meta?.page_count ?? 1}
              totalRows={meta?.total ?? 0}
              windowSize={5}
              disabled={query.isFetching}
              onChange={onPageChange}
              testId="audit-warm-pagination-top"
            />
          </div>
        ) : null}
        <div className="overflow-x-auto">
          <table
            className="min-w-full divide-y divide-pw-border text-sm"
            data-testid="audit-warm-table"
          >
            <thead className="bg-pw-surface text-xs uppercase tracking-wide text-pw-text-muted">
              <tr>
                <th className="px-3 py-2 text-left font-medium">{t('audit.columns.timestamp')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('audit.columns.actor')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('audit.columns.role')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('audit.columns.entity')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('audit.columns.entityId')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('audit.columns.action')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('audit.columns.ip')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('audit.columns.userAgent')}</th>
                <th className="px-3 py-2 text-center font-medium">{t('audit.columns.diff')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('audit.columns.severity')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-pw-border">
              {isInitialLoading ? (
                <tr>
                  <td
                    colSpan={10}
                    className="px-3 py-8 text-center text-pw-text-muted"
                  >
                    {t('audit.loading')}
                  </td>
                </tr>
              ) : isEmpty ? (
                <tr>
                  <td
                    colSpan={10}
                    className="px-3 py-12 text-center"
                    data-testid="audit-warm-empty"
                  >
                    <div className="mx-auto flex max-w-md flex-col items-center gap-2 text-pw-text-muted">
                      <span className="text-base font-medium text-pw-text">
                        {t('audit.empty.title')}
                      </span>
                      <span className="text-sm">
                        {t('audit.empty.body')}
                      </span>
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr
                    key={row.id}
                    className="cursor-pointer hover:bg-pw-surface"
                    onClick={() => onRowClick(row)}
                    data-testid={`audit-warm-row-${row.id}`}
                  >
                    <td className="px-3 py-2 align-top">
                      <span className="block text-pw-text">
                        {t('audit.relativeAgo', { value: fmtRelative(row.occurred_at) })}
                      </span>
                      <span className="block font-mono text-[11px] text-pw-text-muted">
                        {fmtTimestamp(row.occurred_at)}
                      </span>
                    </td>
                    {/* #2653: prefer the actor email (backfilled server-side);
                        widen the cell so the full address is readable. Fall back
                        to the user id only when no email is resolvable. */}
                    <td className="px-3 py-2 align-top min-w-[220px]">
                      {row.actor_email ? (
                        <span
                          className="block break-all text-xs text-pw-text"
                          title={row.actor_email}
                        >
                          {row.actor_email}
                        </span>
                      ) : (
                        <span
                          className="font-mono text-xs text-pw-text-muted"
                          title={row.actor_user_id ?? ''}
                        >
                          {truncate(row.actor_user_id, 12)}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <span className="inline-flex items-center rounded-md border border-pw-border bg-pw-surface px-1.5 py-0.5 text-[11px] font-medium text-pw-text">
                        {actorRoleLabel(t, row.actor_role)}
                      </span>
                    </td>
                    <td className="px-3 py-2 align-top text-pw-text">
                      {entityTypeLabel(t, row.target_type)}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <span
                        className="font-mono text-[11px] text-pw-text-muted"
                        title={row.target_id}
                      >
                        {truncate(row.target_id, 16)}
                      </span>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <span className="inline-flex items-center rounded-md border border-pw-border bg-pw-surface px-1.5 py-0.5 text-[11px] text-pw-text" title={row.action}>
                        {actionLabel(t, row.action)}
                      </span>
                    </td>
                    <td className="px-3 py-2 align-top font-mono text-xs text-pw-text-muted">
                      {row.ip ?? '—'}
                    </td>
                    <td
                      className="px-3 py-2 align-top text-xs text-pw-text-muted"
                      title={row.user_agent ?? undefined}
                    >
                      {truncate(row.user_agent, 28)}
                    </td>
                    <td className="px-3 py-2 text-center align-top">
                      {row.diff && row.diff.length > 0 ? (
                        <span
                          className="inline-flex items-center rounded-md border border-pw-border bg-pw-surface px-1.5 py-0.5 text-[11px] font-medium text-pw-text"
                          aria-label={t('audit.diff.changesAria', { count: row.diff.length })}
                          title={t('audit.diff.viewTooltip')}
                        >
                          {row.diff.length}
                          {row.diff_truncated ? '+' : ''}
                        </span>
                      ) : (
                        <span className="text-xs text-pw-text-muted">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <span
                        className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[11px] font-medium ${severityBadge(row.severity)}`}
                      >
                        {severityLabel(t, row.severity)}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!isInitialLoading && (rows.length > 0 || (meta?.total ?? 0) > 0) ? (
          <div className="border-t border-pw-border px-3 py-2">
            <Pagination
              currentPage={meta?.page ?? page}
              pageCount={meta?.page_count ?? 1}
              totalRows={meta?.total ?? 0}
              windowSize={5}
              disabled={query.isFetching}
              onChange={onPageChange}
              testId="audit-warm-pagination"
            />
          </div>
        ) : null}
      </section>
    </div>
  );
}

export default AuditLogTable;
